import { CalendarDay } from 'further-ui/utils';
import api from 'lib/trpcClient';
import { useEffect, useState } from 'react';

type ShareCapitalAllocation = {
  commitmentId: string;
  amountLcy: number;
  investorLabel: string;
  totalCommitmentLcy: number;
  unallocatedCommitmentLcy: number;
  allocationPercentage: number;
  recallableDistributionLcy: number;
  availableToBeCalledLcy: number;
  excludeFromAllocation: boolean;
  closeDate: CalendarDay;
  fundsCalled: {
    fromCommitmentLcy: number;
    fromRecallableDistributionLcy: number;
  };
};

export default function useAllocateShareCapital({
  vehicleId,
  onSuccess,
  existingBreakdown,
  excludedCommitmentIds,
}: {
  vehicleId: string;
  onSuccess: (allocations: Array<ShareCapitalAllocation>) => void;
  existingBreakdown: Array<{
    commitmentId: string;
    amountLcy: number;
    fundsCalled?: {
      fromCommitmentLcy: number;
      fromRecallableDistributionLcy: number;
    };
  }>;
  excludedCommitmentIds: Array<string>;
}) {
  const [shareCapitalAllocations, setShareCapitalAllocations] = useState<
    Array<ShareCapitalAllocation>
  >([]);

  const { mutate, isPending } =
    api.capitalAction.allocateShareCapital.useMutation({
      onSuccess: (allocations) => {
        setShareCapitalAllocations(allocations);
        onSuccess(allocations);
      },
    });

  const allocateShareCapital = async ({
    useExistingBreakdown,
  }: {
    useExistingBreakdown: boolean;
  }) => {
    if (isPending) {
      return;
    }

    mutate({
      vehicleId,
      excludedCommitmentIds,
      existingBreakdown:
        useExistingBreakdown && existingBreakdown ? existingBreakdown : [],
    });
  };

  const hasAllocationsLoaded = !!shareCapitalAllocations.length;

  useEffect(() => {
    allocateShareCapital({
      useExistingBreakdown: !hasAllocationsLoaded,
    });
  }, [excludedCommitmentIds, hasAllocationsLoaded]);

  return {
    shareCapitalAllocations,
    isPending,
    allocateShareCapital,
  };
}
